import {
  AiStatus,
  ALLOWED_CLASS_SET,
  EXPLICIT_STATUS_CLASS,
  INLINE_TAGS,
  STATUS_KEYWORDS,
  STRUCTURAL_TABLE_TAGS
} from './chat-html-contract';

export class ChatInlineContentNormalizer {
  copy(source: Element, target: Element, doc: Document): void {
    source.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        target.appendChild(doc.createTextNode(child.textContent ?? ''));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const childElement = child as Element;
      const tag = childElement.tagName;
      if (tag === 'BR') {
        target.appendChild(doc.createElement('br'));
        return;
      }
      if (INLINE_TAGS.has(tag)) {
        const clone = doc.createElement(tag.toLowerCase());
        if (tag === 'A') {
          this.copySafeAnchorAttributes(childElement, clone);
        }
        this.applyContractClasses(childElement, clone);
        this.copy(childElement, clone, doc);
        target.appendChild(clone);
        return;
      }
      this.copy(childElement, target, doc);
    });
  }

  cloneTableStructure(node: Node, doc: Document): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? '').trim() ? doc.createTextNode(node.textContent ?? '') : null;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const element = node as Element;
    if (!STRUCTURAL_TABLE_TAGS.has(element.tagName)) {
      return null;
    }
    const clone = doc.createElement(element.tagName.toLowerCase());
    for (const attribute of ['colspan', 'rowspan']) {
      const value = element.getAttribute(attribute);
      if (value) {
        clone.setAttribute(attribute, value);
      }
    }
    if (element.tagName === 'TD' || element.tagName === 'TH') {
      this.copy(element, clone, doc);
    } else {
      element.childNodes.forEach((child) => {
        const normalized = this.cloneTableStructure(child, doc);
        if (normalized) {
          clone.appendChild(normalized);
        }
      });
    }
    return clone;
  }

  copySafeAnchorAttributes(source: Element, target: Element): void {
    const href = source.getAttribute('href');
    if (href && /^https?:\/\//i.test(href)) {
      target.setAttribute('href', href);
      target.setAttribute('target', '_blank');
      target.setAttribute('rel', 'noopener noreferrer');
    }
  }

  explicitStatus(element: Element): AiStatus | null {
    for (const className of Array.from(element.classList)) {
      const status = EXPLICIT_STATUS_CLASS[className];
      if (status) {
        return status;
      }
    }
    return null;
  }

  detectStatus(text: string): AiStatus | null {
    const value = (text || '').trim();
    if (!value) {
      return null;
    }
    return STATUS_KEYWORDS.find(({ pattern }) => pattern.test(value))?.status ?? null;
  }

  parseKeyValue(text: string): { label: string; value: string } | null {
    const normalized = (text || '').replace(/\s+/g, ' ').trim();
    const colonIndex = normalized.indexOf(':');
    if (!normalized || colonIndex <= 0 || colonIndex >= normalized.length - 1) {
      return null;
    }
    const label = normalized.slice(0, colonIndex).trim();
    const value = normalized.slice(colonIndex + 1).trim();
    if (!label || !value || label.length > 48 || label.split(' ').length > 6) {
      return null;
    }
    if (value.length > 64 || value.split(' ').length > 6) {
      return null;
    }
    return { label, value };
  }

  private applyContractClasses(source: Element, target: Element): void {
    source.classList.forEach((className) => {
      if (ALLOWED_CLASS_SET.has(className)) {
        target.classList.add(className);
      }
    });
  }
}
